import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

export const AccountDeletionRequests = () => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/management/account-deletions");
      setItems(data.requests || []);
    } catch { /* non-admin roles get 403; hide silently */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const process = async (r) => {
    if (!window.confirm(`Permanently delete the account and personal files for ${r.email}? This cannot be undone.`)) return;
    setBusy(r.id);
    try {
      const { data } = await API.post(`/management/account-deletions/${r.id}/process`);
      toast.success(data.status === "completed" ? "Account deleted and files purged" : "No account exists for that email");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Deletion failed");
    } finally { setBusy(null); }
  };

  if (!items.length) return null;
  return (
    <Card className="border border-zinc-200" data-testid="account-deletion-requests">
      <CardContent className="p-4 space-y-3">
        <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
          <Trash2 className="w-4 h-4 text-red-600" /> Account Deletion Requests
        </h2>
        {items.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-3 border-t border-zinc-100 pt-3"
            data-testid={`deletion-request-${r.id}`}>
            <div className="text-sm">
              <div className="font-medium text-zinc-900">{r.name} · {r.email}</div>
              <div className="text-zinc-500">{r.company} · {new Date(r.created_at).toLocaleDateString()}</div>
            </div>
            {r.status === "pending" ? (
              <Button size="sm" disabled={busy === r.id} onClick={() => process(r)}
                className="bg-red-600 hover:bg-red-700 text-white" data-testid={`process-deletion-${r.id}`}>
                {busy === r.id ? "Deleting…" : "Delete account"}
              </Button>
            ) : (
              <span className="text-xs uppercase tracking-wide text-zinc-500" data-testid={`deletion-status-${r.id}`}>
                {r.status.replace("_", " ")}
              </span>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
